<template lang="pug">
    //- Full rows (a category and/or a description) use the canonical PostOptionPreview grid so
    //- they match the post select exactly. A bare row (no category AND no description) can't fill
    //- that grid without the title drifting below the thumbnail, so it renders as a single
    //- vertically-centred row instead: thumbnail ↔ name aligned, Nr + price stacked on the right.
    .post-preview(v-if="!compact")
        .image
            figure
                img(v-if="image" :src="image" :title="title")
                i.el-icon-picture-outline(v-else)
        .meta(v-if="topic || aux")
            small {{ topic }}
            small {{ aux }}
        .title
            h3(v-line-clamp:21="1") {{ title }}
            span.price(v-if="titleAux") {{ titleAux }}
        .bait(v-if="teaser" v-line-clamp:18="2")
            p.text {{ teaser }}
    .product-compact(v-else)
        //- No thumbnail for a bare row (an order has no image — a placeholder icon would only
        //- add noise). Reference on top, its meta (date · total) stacked right beneath it.
        figure.image(v-if="image")
            img(:src="image" :title="title")
        .body
            h3.title(v-line-clamp:21="1") {{ title }}
            small.meta(v-if="aux") {{ aux }}
            span.price(v-if="titleAux") {{ titleAux }}
</template>

<script>
// A product / order option row. Mirrors the canonical PostOptionPreview (circular thumbnail,
// muted meta, bold title, optional teaser) so a product select reads like the post select. The
// only product-specific placements: the Nr in the meta's right slot and the price on the title
// row. A "compact" row (no category and no description) drops to a single centred line so the
// name aligns with the thumbnail instead of drifting under it.
export default {
    name: 'ProductOptionPreview',
    props: {
        image: { type: String, default: '' },
        // Meta row, left (post: category; product: category name).
        topic: { type: String, default: '' },
        // Meta row, right (post: date; product: Nr; order: total).
        aux: { type: String, default: '' },
        title: { type: String, required: true },
        // Title row, right — the product price, beside the name (equal weight).
        titleAux: { type: String, default: '' },
        // Optional teaser block (post: teaser; product: description).
        teaser: { type: String, default: '' }
    },
    computed: {
        // Nothing to fill the grid's meta row or teaser row → use the single-line layout.
        compact() {
            return !this.topic && !this.teaser
        }
    }
}
</script>

<style lang="sass" scoped>
// Grid layout: copied verbatim from PostOptionPreview (same columns, widths, margins, circular
// thumbnail); the only addition is `.title` becoming a name+price row and the placeholder icon.
.post-preview
    display: grid
    grid-template-rows: auto auto auto
    grid-template-columns: 32px auto
    grid-column-gap: .4rem
    .meta
        color: $color-grey-text
        font-weight: initial
        display: flex
        align-items: baseline
        justify-content: space-between
        white-space: initial
        margin-top: 10px
        small
            line-height: 1
            display: block
    .title
        display: flex
        align-items: baseline
        justify-content: space-between
        gap: .5rem
        h3
            margin: 0
            line-height: 1
            max-width: 484px
            min-width: 0
        .price
            flex-shrink: 0
            line-height: 1
            font-weight: bold
            white-space: nowrap
    .image
        grid-row: 1 / 3
        margin-top: .5rem
        margin-bottom: 5px
        figure
            margin: 0
            background: $color-skeleton
            width: 32px
            height: 32px
            border-radius: 50%
            overflow: hidden
            display: flex
            align-items: center
            justify-content: center
            img
                width: 100%
                height: 100%
                object-fit: cover
            i
                color: $color-grey-text
                font-size: 16px
    .bait
        color: $--color-text-regular
        font-weight: initial
        width: 522px
        margin-bottom: 10px
        min-height: 18px
        word-break: normal !important
        grid-row: 3
        grid-column: 1 / 3
        p
            white-space: initial
            margin: 0
            line-height: 1.3

// Bare row (an order): no thumbnail, the reference on top with its meta stacked directly beneath
// (a two-line option) instead of the meta drifting to the far right.
.product-compact
    display: flex
    align-items: center
    gap: .4rem
    padding: 3px 0
    .image
        flex-shrink: 0
        margin: 0
        background: $color-skeleton
        width: 32px
        height: 32px
        border-radius: 50%
        overflow: hidden
        display: flex
        align-items: center
        justify-content: center
        img
            width: 100%
            height: 100%
            object-fit: cover
    .body
        flex: 1
        min-width: 0
        display: flex
        flex-direction: column
        .title
            margin: 0
            line-height: 1.3
        .meta
            color: $color-grey-text
            line-height: 1.3
        .price
            font-weight: bold
            line-height: 1.3
</style>
